// Mock API endpoint for Pilot integration
// In a real implementation, this would be a backend API

export interface PilotSignalRequest {
  signalId: string
  symbol: string
  side: 'LONG' | 'SHORT'
  entry: number
  sl: number
  tp: number
  rr: number
}

export interface PilotSignalResponse {
  success: boolean
  message: string
  pilotOrderId?: string
  error?: string
}

// Mock implementation
export async function forwardSignalToPilot(signalData: PilotSignalRequest): Promise<PilotSignalResponse> {
  try {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000))

    // Mock validation
    if (!signalData.signalId || !signalData.symbol) {
      throw new Error('Invalid signal data')
    }

    // Mock successful response
    const mockOrderId = `PILOT_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    
    console.log('Signal forwarded to Pilot:', {
      signalId: signalData.signalId,
      symbol: signalData.symbol,
      side: signalData.side,
      entry: signalData.entry,
      sl: signalData.sl,
      tp: signalData.tp,
      rr: signalData.rr,
      pilotOrderId: mockOrderId
    })

    return {
      success: true,
      message: 'Signal forwarded to Pilot successfully',
      pilotOrderId: mockOrderId
    }
  } catch (error) {
    console.error('Error forwarding signal to Pilot:', error)
    return {
      success: false,
      message: 'Failed to forward signal to Pilot',
      error: error instanceof Error ? error.message : 'Unknown error'
    }
  }
}

// Mock API endpoints for FCM
export interface FCMSubscriptionRequest {
  token: string
  topic: string
}

export interface FCMUnsubscriptionRequest {
  token: string
  topic: string
}

export interface FCMTestNotificationRequest {
  token: string
  title: string
  body: string
  data?: Record<string, string>
}

export async function subscribeToFCMTopic(request: FCMSubscriptionRequest): Promise<{ success: boolean; message: string }> {
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500))
    
    console.log('FCM Topic Subscription:', request)
    
    return {
      success: true,
      message: `Successfully subscribed to topic: ${request.topic}`
    }
  } catch (error) {
    return {
      success: false,
      message: 'Failed to subscribe to topic'
    }
  }
}

export async function unsubscribeFromFCMTopic(request: FCMUnsubscriptionRequest): Promise<{ success: boolean; message: string }> {
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500))
    
    console.log('FCM Topic Unsubscription:', request)
    
    return {
      success: true,
      message: `Successfully unsubscribed from topic: ${request.topic}`
    }
  } catch (error) {
    return {
      success: false,
      message: 'Failed to unsubscribe from topic'
    }
  }
}

export async function sendFCMTestNotification(request: FCMTestNotificationRequest): Promise<{ success: boolean; message: string }> {
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    console.log('FCM Test Notification:', request)
    
    // In a real implementation, this would send an actual push notification
    // For now, we'll just log it and simulate success
    
    return {
      success: true,
      message: 'Test notification sent successfully'
    }
  } catch (error) {
    return {
      success: false,
      message: 'Failed to send test notification'
    }
  }
}
